import { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react';

type PopoverPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';

function usePopover(position: PopoverPosition = 'bottom-right', useTriggerWidth = false): {
  isOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef: React.RefObject<HTMLButtonElement>;
} {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const scrollableParentsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const scrollableParents: HTMLElement[] = [];
      let parent = triggerRef.current.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflow + style.overflowY + style.overflowX)) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }

      scrollableParentsRef.current = scrollableParents;

      const handleScroll = (): void => {
        setIsOpen(false);
      };

      scrollableParents.forEach((p) =>
        p.addEventListener('scroll', handleScroll, { passive: true }),
      );

      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        scrollableParents.forEach((p) =>
          p.removeEventListener('scroll', handleScroll),
        );
        window.removeEventListener('scroll', handleScroll);
      };
    }
    return () => {};
  }, [isOpen]);

  const updatePopoverPosition = useCallback((): void => {
    if (isOpen && triggerRef.current && menuRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();

      menuRef.current.style.width = useTriggerWidth ? `${triggerRect.width}px` : 'auto';
      menuRef.current.style.maxWidth = useTriggerWidth ? `${triggerRect.width}px` : '200px';

      const menuRect = menuRef.current.getBoundingClientRect();

      let computedPosition = position;

      let top = 0;
      let left = 0;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Helper functions to calculate positions
      const calculateTop = (pos: PopoverPosition): number => {
        if (pos.includes('top')) {
          return triggerRect.top - menuRect.height;
        }
        if (pos.includes('bottom')) {
          return triggerRect.bottom;
        }
        return triggerRect.bottom;
      };

      const calculateLeft = (pos: PopoverPosition): number => {
        if (pos.includes('left')) {
          return triggerRect.left;
        }
        if (pos.includes('right')) {
          return triggerRect.right - menuRect.width;
        }
        if (pos.includes('center')) {
          return triggerRect.left + triggerRect.width / 2 - menuRect.width / 2;
        }
        return triggerRect.left;
      };

      // Initial position calculations
      top = calculateTop(computedPosition);
      left = calculateLeft(computedPosition);

      if (top < 0) {
        if (computedPosition.includes('top')) {
          computedPosition = computedPosition.replace('top', 'bottom') as PopoverPosition;
        } else {
          computedPosition = `bottom-${computedPosition.split('-')[1]}` as PopoverPosition;
        }
        top = calculateTop(computedPosition);
      } else if (top + menuRect.height > viewportHeight) {
        if (computedPosition.includes('bottom')) {
          computedPosition = computedPosition.replace('bottom', 'top') as PopoverPosition;
        } else {
          computedPosition = `top-${computedPosition.split('-')[1]}` as PopoverPosition;
        }
        top = calculateTop(computedPosition);
      }

      if (left < 0) {
        if (computedPosition.includes('left')) {
          computedPosition = computedPosition.replace('left', 'right') as PopoverPosition;
        } else {
          computedPosition = computedPosition.includes('center')
            ? computedPosition
            : `${computedPosition.split('-')[0]}-right` as PopoverPosition;
        }
        left = calculateLeft(computedPosition);
      } else if (left + menuRect.width > viewportWidth) {
        if (computedPosition.includes('right')) {
          computedPosition = computedPosition.replace('right', 'left') as PopoverPosition;
        } else {
          computedPosition = computedPosition.includes('center')
            ? computedPosition
            : `${computedPosition.split('-')[0]}-left` as PopoverPosition;
        }
        left = calculateLeft(computedPosition);
      }

      if (left < 0) {
        left = 0;
      } else if (left + menuRect.width > viewportWidth) {
        left = viewportWidth - menuRect.width;
      }

      if (top < 0) {
        top = 0;
      } else if (top + menuRect.height > viewportHeight) {
        top = viewportHeight - menuRect.height;
      }

      menuRef.current.style.position = 'fixed';
      menuRef.current.style.top = `${top}px`;
      menuRef.current.style.left = `${left}px`;
      menuRef.current.style.zIndex = '9999';
    }
  }, [isOpen, position, useTriggerWidth]);

  useLayoutEffect(updatePopoverPosition, [isOpen, position, useTriggerWidth, updatePopoverPosition]);

  useEffect(() => {
    const handleResize = (): void => {
      updatePopoverPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, position, useTriggerWidth, updatePopoverPosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        menuRef.current
        && !menuRef.current.contains(event.target as Node)
        && !triggerRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    isOpen,
    menuRef,
    setIsOpen,
    triggerRef,
  };
}

export default usePopover;
